import {html} from 'lit';
import type {
  Cmd,
  ImmutableProgram,
  InitialProperties,
  ImmutableResult,
  UpdateHandler,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {VariantsFor} from '@gl/lit/types/architecture.js';
import {
  ModalCancelEvent,
  ModalCloseEvent,
  ModalConfirmEvent,
  ModalOpenEvent,
} from './utils.js';
import type {
  Intent,
  Variant as ButtonVariant,
} from '@gl/lit/components/base/button/index.js';
import type {Fill, Size} from '@gl/lit/components/base/icon/index.js';
import {classMap} from 'lit/directives/class-map.js';

// -- Component dependencies

import '@gl/lit/components/base/modal/index.js';
import '@gl/lit/components/base/icon/index.js';
import '@gl/lit/components/base/button/index.js';

// -- Types

export type Model = {
  open: boolean;
  closeOnConfirm: boolean;
  confirmIntent: Intent;
  cancelIntent: Intent;
  icon: string;
  iconFill: Fill;
  iconSize: Size;
  confirmVariant: ButtonVariant;
  cancelVariant: ButtonVariant;
  messageIntent: Intent;
};

export const Msg = makeVariants({
  open: Variant<void>()('open'),
  close: Variant<void>()('close'),
  cancel: Variant<void>()('cancel'),
  confirm: Variant<void>()('confirm'),
});

export type Msg = VariantsFor<typeof Msg>;

// -- Init

function init(
  props: InitialProperties,
  _cmd: Cmd,
  _model?: Model
): ImmutableResult<Model> {
  const open = <boolean>props.get('open') ?? false;
  const closeOnConfirm = <boolean>props.get('closeOnConfirm') ?? false;
  const confirmIntent = <Intent>props.get('confirmIntent') ?? 'danger';
  const cancelIntent = <Intent>props.get('cancelIntent') ?? 'secondary';
  const confirmVariant =
    <ButtonVariant>props.get('confirmVariant') ?? 'outlined';
  const cancelVariant = <ButtonVariant>props.get('cancelVariant') ?? 'outlined';
  const messageIntent = <Intent>props.get('messageIntent') ?? 'danger';
  const icon = <string>props.get('icon') ?? 'error';
  const iconFill = <Fill>props.get('iconFill') ?? 'red';
  const iconSize = <Size>props.get('iconSize') ?? '2xl';

  return {
    open,
    closeOnConfirm,
    confirmIntent,
    cancelIntent,
    confirmVariant,
    cancelVariant,
    messageIntent,
    icon,
    iconFill,
    iconSize,
  };
}

// -- Update

function update(msg: Msg, model: Model, cmd: Cmd): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'close':
      return [
        {...model, open: false},
        cmd(host => {
          host.dispatchEvent(ModalCloseEvent.create());
        }),
      ];

    case 'open': {
      return [
        {...model, open: true},
        cmd(host => {
          host.dispatchEvent(ModalOpenEvent.create());
        }),
      ];
    }

    case 'confirm': {
      return [
        {...model, open: model.closeOnConfirm ? false : model.open},
        cmd(host => {
          host.dispatchEvent(ModalConfirmEvent.create());
          if (model.closeOnConfirm) {
            host.dispatchEvent(ModalCloseEvent.create());
          }
        }),
      ];
    }

    case 'cancel': {
      return [
        {...model, open: false},
        cmd(host => {
          host.dispatchEvent(ModalCancelEvent.create());
          host.dispatchEvent(ModalCloseEvent.create());
        }),
      ];
    }
  }
}

// -- View
function view(model: Model, handleUpdate: UpdateHandler<Model, Msg>) {
  return html` <fly-modal ?open=${model.open}>
    <div slot="header-aside">
      <fly-icon icon="close" size="sm"></fly-icon>
    </div>

    <div class="confirm-modal">
      <div class="confirm-modal__message">
        <div class="confirm-modal__illustration">
          <fly-icon
            icon="${model.icon}"
            fill="${model.iconFill}"
            size="${model.iconSize}"
          >
          </fly-icon>
        </div>
        <div
          class=${classMap({
            'confirm-modal__title': true,
            'confirm-modal__title__danger': model.messageIntent === 'danger',
            'confirm-modal__title__success': model.messageIntent === 'success',
            'confirm-modal__title__base': model.messageIntent === 'base',
          })}
        >
          <slot></slot>
        </div>
        <div class="confirm-modal__more-info">
          <slot name="more-info"></slot>
        </div>
      </div>

      <div class="confirm-modal__actions">
        <div class="confirm-modal__cancel-btn">
          <fly-button
            blurOnClick
            variant=${model.cancelVariant}
            intent=${model.cancelIntent}
            @click=${() => {
              handleUpdate(Msg.cancel());
            }}
          >
            <slot name="cancel"> Cancel </slot>
          </fly-button>
        </div>
        <div class="confirm-modal__confirm-btn">
          <fly-button
            blurOnClick
            intent=${model.confirmIntent}
            variant=${model.confirmVariant}
            @click=${() => {
              handleUpdate(Msg.confirm());
            }}
          >
            <slot name="confirm"> Confirm </slot>
          </fly-button>
        </div>
      </div>
    </div>
  </fly-modal>`;
}

// -- Program

export var main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  view,
};
